/**
 * The header of every scan page (DEC-194 decision 2): a breadcrumb back to Scans, the
 * repository, its branch or source, the status pill and the scan's tabs.
 *
 * Tabs are links in a labelled navigation group with `aria-current="page"`, not ARIA
 * tabs, because each one is its own page and URL. They come from `scanTabsFor`, so they
 * never offer a view the profile cannot open.
 */
import { Link } from 'react-router-dom'
import type { Profile, ScanVisibility } from '../../types/api'
import { VisibilityControl } from './VisibilityControl'
import { PillDot, TonePill } from '../shared/Badge'
import { StatusPill } from '../scans/ScanRow'
import { scanTabsFor, type ScanNavStatus, type ScanTabId } from './scanTabs'

interface ScanHeaderProps {
  scanId: string
  /** The repository name, or null while neither the scan nor a remembered copy is known. */
  repo: string | null
  /** "GitHub · main", "Uploaded folder". */
  source: string | null
  status: ScanNavStatus | null
  profile: Profile | null
  current: ScanTabId | null
  /** DEC-196: shown, with its menu, to the scan's owner only. */
  visibility?: ScanVisibility | null
  isOwner?: boolean
  workspaceName?: string | null
  /** DEC-196: a GitHub push scan names who pushed, as a label. */
  pushedBy?: string | null
}

export function ScanHeader({
  scanId,
  repo,
  source,
  status,
  profile,
  current,
  visibility,
  isOwner = false,
  workspaceName,
  pushedBy,
}: ScanHeaderProps) {
  const tabs = scanTabsFor(profile, status)
  const running = status === 'running'
  return (
    <header data-testid="scan-header" className="flex-none border-b border-hairline bg-panel px-7 pt-4">
      <nav aria-label="Breadcrumb" className="flex items-center gap-[6px] text-[12px]">
        <Link to="/scans" className="font-semibold text-accent-text hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent rounded-sm">
          Scans
        </Link>
        <span aria-hidden className="text-faint">/</span>
        <span className="min-w-0 truncate text-muted">{repo ?? 'Scan'}</span>
      </nav>

      <div className="mt-[10px] flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="min-w-0 truncate text-[20px] font-semibold leading-tight text-ink">{repo ?? 'Loading scan'}</span>
        {status === 'deleted' ? (
          <TonePill tone="stopped" testId="status-pill">Deleted permanently</TonePill>
        ) : status ? (
          <StatusPill status={status} />
        ) : null}
        {visibility && isOwner && status !== 'deleted' && (
          <VisibilityControl scanId={scanId} visibility={visibility} workspaceName={workspaceName} />
        )}
        {source && <span className="text-[12px] text-muted">{source}</span>}
        {pushedBy && <span className="text-[12px] text-muted">Pushed by @{pushedBy}</span>}
      </div>

      {tabs.length > 0 ? (
        <nav aria-label="This scan" className="mt-[10px] flex flex-wrap gap-1">
          {tabs.map((t) => {
            const on = t.id === current
            return (
              <Link
                key={t.id}
                to={t.path(scanId)}
                aria-current={on ? 'page' : undefined}
                className={`inline-flex items-center gap-[6px] border-b-2 px-3 pb-[10px] pt-2 text-[13px] transition-colors rounded-t-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent ${
                  on ? 'border-accent font-semibold text-ink' : 'border-transparent font-medium text-muted hover:text-ink'
                }`}
              >
                {t.label}
                {t.id === 'progress' && running && (
                  <>
                    <PillDot tone="running" size={6} pulse />
                    <span className="sr-only">(running)</span>
                  </>
                )}
              </Link>
            )
          })}
        </nav>
      ) : (
        <div className="h-4" />
      )}
    </header>
  )
}
